/**
 * Buyer — the storefront visitor as a persistent record: one document per
 * anonymous auth uid at `workspaces/{orgId}/buyers/{buyerId}`. The document
 * id IS that uid, so nothing inside the document restates it and every door
 * — the buyer's own read and every write alike — pins `request.auth.uid ==
 * buyerId` against the path. The identity exists already: the app signs
 * every visitor in at boot so a checkout can be owned, and this collection
 * materializes the person `checkouts.buyerUid` and `orders.buyerUid`
 * point at.
 *
 * `timeline/{timelineEventId}` is the raw truth under every derived number —
 * append-only events the visitor's own browser writes as a page is entered, a
 * basket settles, or a ticket is retrieved, beside the cart milestones the
 * Admin SDK writes as a checkout moves. Each carries `at` and the
 * `expiresAt` the collection-group TTL collects on 90 days later. Nothing is
 * materialized from it: a visit is a gap between consecutive `at` values,
 * grouped when a report is asked for. `analyticsDaily/{date}` caches those
 * derivations for closed days at the default gap — deletable and
 * recomputable while the raw window survives, never a second truth.
 *
 * The timeline wildcard is `{timelineEventId}`, not `{eventId}`: `eventId`
 * names the concert on the timeline event's own field and everywhere else in
 * this schema, and a rule saying `eventId` must mean exactly one thing.
 *
 * Every write to a buyer document and its timeline lives in a hand fragment
 * (`ican.extraMatches`), so `entity()` here declares reads only and nothing
 * generated competes with it. A generated write rule derives its field mask
 * from pinned values, which cannot express what these documents need:
 * per-variant key closure over a discriminated union, `at == request.time`
 * beside `expiresAt == request.time + 90 days`, a buyer update masked to the
 * three mutable fields while `startedAt` and the first-touch pair stay
 * immutable, and a length cap on every free string. `Checkout` and
 * `WorkspaceSettings` stand the same way.
 *
 * The reads: a buyer reads their own document by id — a path variable
 * resolves to nothing outside Firestore, so `me.can` answers false in the
 * browser and this rule is the Firestore door alone, exactly as `User`'s own
 * read is. Every operator read — buyers, their timelines, the daily cache —
 * gates on `tickets.reports.view` + mfa, the door every other
 * buyer-identifying read in this schema already stands behind.
 * `analyticsDaily` has no client writer at all: the report callable writes it
 * through the Admin SDK.
 */

import { entity } from "@mhmo91/ican";
import { firestore } from "@mhmo91/ican/firestore/binding";
import { LocaleEnum } from "@momo/i18n";
import type { Timestamp } from "firebase/firestore";
import { z } from "zod";
import { RefusalReasonSchema } from "./checkout.entity.ts";
import { Cents, Qty } from "./ticket-type.entity.ts";
import type { Actor } from "./workspace.entity.ts";

/** A calendar day in the venue's own zone — the key `analyticsDaily` is
 *  stored at and the unit every report range is expressed in. */
export const CalendarDay = z
	.string()
	.regex(/^\d{4}-\d{2}-\d{2}$/)
	.describe(
		"one calendar day in the venue's zone, the unit a report range is expressed in and a cached row is keyed by",
	);

/** The silence that separates one visit from the next. Bounded above by the
 *  heartbeat's own hard stop: a tab beats for at most four hours, so no gap
 *  wider than that can ever fall inside one visit's evidence. */
export const VisitGapMinutes = z
	.number()
	.int()
	.min(1)
	.max(240)
	.describe(
		"the minutes of silence that end a visit — the one derivation knob, bounded above by the heartbeat's four-hour hard stop, past which no gap can sit inside a single visit's evidence",
	);

/** A count or a whole-second duration — never negative, never fractional. */
const Count = z.number().int().min(0);

/**
 * The storefront surfaces a visitor can be on. Its own closed vocabulary,
 * deliberately not the router's key set: the browser maps route keys onto
 * these five, and operator routes map onto nothing at all — the console is
 * never measured.
 */
export const PageSchema = z
	.enum(["landing", "event", "order", "refund", "legal"])
	.describe(
		"the storefront surface a visitor is on — the closed set the browser maps its routes onto; operator routes map to none of them and emit nothing",
	);
export type Page = z.infer<typeof PageSchema>;

/** Coarse client facts useful for compatibility decisions without retaining a
 * raw user-agent string, exact dimensions, or a device identifier. */
export const ClientOsSchema = z.enum([
	"ios",
	"android",
	"macos",
	"windows",
	"linux",
	"chromeos",
	"other",
]);
export const ClientBrowserSchema = z.enum([
	"safari",
	"chrome",
	"firefox",
	"edge",
	"samsung",
	"other",
]);
export const ClientDeviceSchema = z.enum([
	"phone",
	"tablet",
	"desktop",
	"other",
]);
export const ClientViewportSchema = z.enum(["xs", "sm", "md", "lg", "xl"]);
export const ClientOrientationSchema = z.enum(["portrait", "landscape"]);

export const ClientProfileSchema = z
	.object({
		os: ClientOsSchema,
		browser: ClientBrowserSchema,
		device: ClientDeviceSchema,
		viewport: ClientViewportSchema,
		orientation: ClientOrientationSchema,
	})
	.strict();
export type ClientProfile = z.infer<typeof ClientProfileSchema>;

const ClientCountsSchema = z
	.object({
		os: z.partialRecord(ClientOsSchema, Count).default({}),
		browser: z.partialRecord(ClientBrowserSchema, Count).default({}),
		device: z.partialRecord(ClientDeviceSchema, Count).default({}),
		viewport: z.partialRecord(ClientViewportSchema, Count).default({}),
		orientation: z.partialRecord(ClientOrientationSchema, Count).default({}),
	})
	.strict();
export type ClientCounts = z.infer<typeof ClientCountsSchema>;

/** The completeness status carried by each metric in a daily report row. */
export const CoverageSchema = z.enum(["complete", "partial", "unavailable"]);
export type Coverage = z.infer<typeof CoverageSchema>;

/** Coverage for the four metric groups a client-filtered report answers. */
export const ReportCoverageSchema = z
	.object({
		clientSpecs: CoverageSchema,
		visits: CoverageSchema,
		funnel: CoverageSchema,
		revenue: CoverageSchema,
	})
	.strict();
export type ReportCoverage = z.infer<typeof ReportCoverageSchema>;

/** The visit-entry semantics version used to derive client-filtered rows. */
export const ClientSemanticsVersionSchema = z.literal(1).nullable();
export type ClientSemanticsVersion = z.infer<
	typeof ClientSemanticsVersionSchema
>;

/**
 * How far a visit got. Ordered by depth, so the deepest stage a visit
 * reached is the one it is counted at, and `droppedAt` keys on this same
 * vocabulary minus `paid` — a visit that paid dropped nowhere.
 */
export const FunnelStageSchema = z
	.enum(["landing", "event", "contact", "payment", "paid"])
	.describe(
		"how deep a visit got, in order — the deepest stage a visit reached is the one it counts at",
	);
export type FunnelStage = z.infer<typeof FunnelStageSchema>;

/**
 * What a buyer did with tickets they already hold. Snake-cased where a
 * wallet vendor's own name is part of the token, which is the value's
 * spelling on disk, not a field name.
 */
export const OrderActionSchema = z
	.enum(["download", "wallet_apple", "wallet_google", "ics", "handover"])
	.describe(
		"the way a buyer took delivery of tickets they already hold — which retrieval channels earn their place derives from these counts",
	);
export type OrderAction = z.infer<typeof OrderActionSchema>;

/**
 * A cart's passage through the funnel — the three it deepens through in order,
 * then the three ways it ends without paying. Its own vocabulary, neither the
 * checkout document's status (which knows nothing of contact, and collapses
 * every non-payment ending into one terminal state) nor the visit-depth
 * vocabulary FunnelStageSchema names, which counts browsing a visit never
 * carried into a cart.
 */
export const CheckoutStageSchema = z
	.enum(["contact", "authorising", "paid", "refused", "abandoned", "expired"])
	.describe(
		"the milestone a cart reached — contact given, authorisation begun, payment taken, and the three ways a cart ends without paying: refused by the seller, left by the buyer, or timed out",
	);
export type CheckoutStage = z.infer<typeof CheckoutStageSchema>;

/** The length a first-touch value is truncated to before it is written —
 *  the browser trims to these, and the door refuses anything longer. */
export const REFERRER_MAX_LENGTH = 200;
export const UTM_VALUE_MAX_LENGTH = 100;

/**
 * First touch — the five campaign parameters, each length-capped and each
 * absent on an arrival that carried it. Closed rather than an open map: the
 * door admits these names and no others, so a sixth parameter a visitor
 * arrives with is dropped by the browser's own capture before the write,
 * instead of costing that visitor their whole record. A checkout's channel
 * records the same five names in its own map.
 */
export const UtmSchema = z
	.object({
		utm_source: z.string().min(1).max(UTM_VALUE_MAX_LENGTH).nullish(),
		utm_medium: z.string().min(1).max(UTM_VALUE_MAX_LENGTH).nullish(),
		utm_campaign: z.string().min(1).max(UTM_VALUE_MAX_LENGTH).nullish(),
		utm_term: z.string().min(1).max(UTM_VALUE_MAX_LENGTH).nullish(),
		utm_content: z.string().min(1).max(UTM_VALUE_MAX_LENGTH).nullish(),
	})
	.strict()
	.describe(
		"the campaign parameters a visitor arrived under — the five the vocabulary names, and no other",
	);
export type Utm = z.infer<typeof UtmSchema>;

/** The parameter names the browser's own capture filters to before it writes
 *  first touch — read off the shape above, never restated beside it. */
export const UTM_KEYS = Object.keys(UtmSchema.shape);

/**
 * The visitor's decision on Meta ad measurement — one decision per browser,
 * the record both reporting legs read before anything loads or sends. Its
 * `at` is pinned to the decision itself; the identifiers that refresh on
 * every consented page load live in BuyerAdsIdsSchema beside it, so "when
 * decided" is never rewritten by a refresh.
 */
export const BuyerAdsConsentSchema = z.object({
	consent: z
		.boolean()
		.describe(
			"the decision — true licenses the Meta tag in the browser and the server conversion send; false is a refusal both legs honor by loading and sending nothing",
		),
	at: z
		.custom<Timestamp>()
		.describe(
			"rules-pinned == request.time whenever this map is written — when the decision was made, pinned to the decision",
		),
	grantedAt: z
		.custom<Timestamp>()
		.nullish()
		.describe(
			"the latest grant, kept across a withdrawal so a send made inside the granted window stays demonstrable after the consent ends; null on a record that has only ever refused",
		),
	version: z
		.string()
		.min(1)
		.max(20)
		.describe(
			"the ADS_CONSENT_VERSION the decision was given against — a decision at any other version is stale: undecided to the browser, withheld to the server task",
		),
	locale: LocaleEnum.describe(
		"the language the prompt was read in when the decision was given",
	),
});
export type BuyerAdsConsent = z.infer<typeof BuyerAdsConsentSchema>;

/**
 * The browser identifiers the server conversion forwards to Meta —
 * browser-bound exactly as the buyer record is, refreshed on every consented
 * page load so a purchase carries its own session's values. They exist only
 * beside `adsConsent.consent:true`: withdrawal nulls them and the delivery
 * task nulls them after a send, so none outlives its one purpose.
 */
export const BuyerAdsIdsSchema = z.object({
	fbp: z
		.string()
		.max(64)
		.nullish()
		.describe(
			"the _fbp cookie Meta's tag set on this browser — the browser id that lets Meta match the server event to the pixel's own",
		),
	fbc: z
		.string()
		.max(400)
		.nullish()
		.describe(
			"the _fbc cookie, or its synthesis from a landing fbclid — the ad-click id that ties this browser's purchase to the ad that brought it; absent when no ad click ever landed here",
		),
	ua: z
		.string()
		.max(512)
		.nullish()
		.describe(
			"navigator.userAgent — the field a website conversion event must carry to be accepted; stored only under consent and only until the send",
		),
});
export type BuyerAdsIds = z.infer<typeof BuyerAdsIdsSchema>;

// workspaces/{orgId}/buyers/{buyerId} — the visitor's own record, tied to
// the browser's anonymous auth identity and reaped after twelve months without a
// purchase.
//
// Fields justified:
//   startedAt       — when this browser first became a buyer; new-vs-returning
//                     reads it
//   lastSeenAt      — the heartbeat; presence and dwell read it
//   referrer, utm   — first touch, the attribution a visit that never buys
//                     has nowhere else to record
//   lastPage        — which surface the visitor is on, for the live panel's
//                     caption
//   lastEventId     — which event, when that surface is an event page, so
//                     the live count splits per event
//   analyticsOptOut — the standing objection, honored before every write
//   adsConsent      — the visitor's decision on Meta ad measurement, the one
//                     record both the browser tag and the server conversion
//                     task read before anything loads or sends
//   adsIds          — the browser identifiers the server conversion forwards
//                     to Meta, alive only beside consent:true
//   adsOptOut       — the standing Art. 21 objection to this visitor's
//                     completed orders being reported to the ad platform,
//                     the switch the privacy page carries wherever a pixel
//                     is configured; the delivery task refuses to send for a
//                     buyer holding it. It stands beside adsConsent/adsIds
//                     rather than replacing them, because the two rest on
//                     different legal bases: a consent decision licenses the
//                     browser script's write to the visitor's device, an
//                     objection answers a server report that touches no
//                     device at all, so neither switch can speak for the
//                     other
// Excluded:
//   uid — the document id is the uid; a mirrored copy is free to disagree
//     with the path both the read and every write already pin against
//   IP, viewport, and every fingerprint-class property — none is processed
//     anywhere in this model. The user agent is the one bounded exception,
//     and only where the operator turned the browser script on
//     (`workspace.ads.meta.browserTag`): adsIds.ua — the field a website
//     conversion event must carry — is stored only under consent, only
//     beside adsConsent.consent:true, and only until the send nulls it.
//     With the script off, no user agent is stored anywhere in this model
//   visit counts, dwell totals, a last-visit stamp — each derives from the
//     timeline at read time, and a stored copy is free to contradict the
//     events it claims to summarize
//   email and name — a buyer who pays carries both on the order they paid for
export const BuyerSchema = z.object({
	startedAt: z
		.custom<Timestamp>()
		.describe(
			"rules-pinned == request.time at create and immutable after — the moment this browser first became a buyer, the age new-versus-returning counts read",
		),
	lastSeenAt: z
		.custom<Timestamp>()
		.describe(
			"rules-pinned == request.time on every update — the visibility-gated heartbeat a visible tab beats; the live panel counts buyers beating inside the last 75 seconds and dwell sums the spans the beats close",
		),
	referrer: z
		.string()
		.max(REFERRER_MAX_LENGTH)
		.nullish()
		.describe(
			"first touch, immutable once written — the origin that sent this buyer here, trimmed to that origin and bounded well past any real one; absent on a direct arrival",
		),
	utm: UtmSchema.nullish().describe(
		"first touch, immutable once written — the campaign parameters this buyer arrived under, the same five names a checkout's own channel records, so a campaign's visits and its purchases join on one vocabulary",
	),
	lastPage: PageSchema.nullish().describe(
		"the storefront surface this buyer most recently entered — the live panel's caption; the ordered sequence lives in the timeline",
	),
	lastEventId: z
		.string()
		.min(1)
		.max(64)
		.nullish()
		.describe(
			"the event whose page this buyer is on — set with an event page_view, cleared to null on any other surface; the live panel's per-event split reads it beside lastSeenAt",
		),
	analyticsOptOut: z
		.boolean()
		.default(false)
		.describe(
			"the visitor's standing objection to measurement, set from the privacy policy's own switch — while it is true the browser writes nothing, neither event nor beat",
		),
	adsConsent: BuyerAdsConsentSchema.nullish().describe(
		"the visitor's decision on Meta ad measurement — absent until the prompt is answered, and read by both reporting legs before either moves",
	),
	adsIds: BuyerAdsIdsSchema.nullish().describe(
		"the forwarded Meta identifiers — present only beside adsConsent.consent:true, refreshed each consented load, nulled on withdrawal and after a send",
	),
	adsOptOut: z
		.boolean()
		.default(false)
		.describe(
			"the visitor's standing Art. 21 objection to their completed orders being reported to the ad platform — the analyticsOptOut shape, a different recipient and a different processing, so a distinct switch; the delivery task refuses to send for a buyer carrying it",
		),
});
export type Buyer = z.infer<typeof BuyerSchema>;
export type BuyerInput = z.input<typeof BuyerSchema>;

// The two stamps every timeline event carries, whatever it is: one clock and
// one expiry. Each variant adds only what its own name means.
const TimelineStamps = z.object({
	at: z
		.custom<Timestamp>()
		.describe(
			"rules-pinned == request.time — the clock every derivation reads: a visit is the run of events between two gaps wider than the knob, and dwell is the span such a run closes",
		),
	expiresAt: z
		.custom<Timestamp>()
		.describe(
			"rules-pinned == request.time + 90 days — the field the timeline collection-group TTL collects on, so behavioural raw data ages out without anyone deleting it",
		),
});

// workspaces/{orgId}/buyers/{buyerId}/timeline/{timelineEventId} — raw,
// append-only, closed. A name that is not one of these four is not writable,
// so a new measurement is a schema change and a rules change, never a string
// a browser invents.
//
// Three of the four are the visitor's own browser writing what it just did.
// `checkout_stage` is the exception: the rules fragment withholds that name
// from every client door, and the Admin SDK — which passes rules — is its only
// writer. A browser able to forge a funnel milestone could forge the funnel.
//
// Variants justified:
//   page_view      — the surface entered; the whole traffic, click-through and
//                    drop-off derivation rests on it
//   basket_change  — the settled basket, so pre-checkout intent is measurable
//                    for a visit that never reaches payment
//   order_action   — how a buyer took delivery, the retrieval-channel split
//   checkout_stage — a cart's passage through the funnel, carrying the cart's
//                    own start instant and channel, so every band is derivable
//                    from the timeline alone and no figure reads a checkout
//                    document that a retention clock will collect
// Excluded:
//   a buyer uid on the event — the parent document's id already is it
//   a visit or session id — the rejected construct: a visit is a gap in `at`,
//     recomputable under any knob, and a stamped id would freeze one
//   a page on basket_change, order_action and checkout_stage — the checkout
//     each names places it, and the preceding page_view dates the arrival
//   scroll depth, clicks, dwell per event — dwell derives from the stamps
//     already here, and the rest is behavioural detail nothing answers a
//     question with
export const TimelineEventSchema = z.discriminatedUnion("name", [
	TimelineStamps.extend({
		name: z.literal("page_view"),
		page: PageSchema,
		eventId: z
			.string()
			.min(1)
			.max(64)
			.nullish()
			.describe(
				"the event whose page was entered — present only on an event page, absent on every other surface",
			),
		checkoutId: z
			.string()
			.min(1)
			.max(64)
			.nullish()
			.describe(
				"the checkout whose order this page shows — present when the browser on this page is the one that paid for that cart; null on a ticket page opened from its access link alone, which carries the capability token and no joinable id",
			),
		utmSource: z
			.string()
			.min(1)
			.max(UTM_VALUE_MAX_LENGTH)
			.nullish()
			.describe(
				"the campaign source this arrival itself carried — last touch, read from the entered URL, so a visitor returning under a second campaign records that one rather than the one that first found them; absent on an arrival with no campaign on it",
			),
		utmCampaign: z
			.string()
			.min(1)
			.max(UTM_VALUE_MAX_LENGTH)
			.nullish()
			.describe(
				"the campaign this arrival itself carried, the axis that separates one campaign from every other sharing its source; absent on an arrival with no campaign on it",
			),
		client: ClientProfileSchema.nullish().describe(
			"coarse client compatibility facts observed on this page view; absent on older events",
		),
	}),
	TimelineStamps.extend({
		name: z.literal("basket_change"),
		checkoutId: z
			.string()
			.min(1)
			.max(64)
			.describe(
				"the checkout this basket belongs to — the join every funnel figure crosses from raw behaviour into checkout and order state",
			),
		eventId: z
			.string()
			.min(1)
			.max(64)
			.describe("the event whose tickets this basket holds"),
		qty: Qty.describe(
			"the basket's whole quantity once the change settles, across every type in it — the storefront never leaves a basket empty, so this is the same quantity vocabulary a checkout's own lines carry",
		),
		valueCents: Cents.describe(
			"the basket's total once the change settles, at the prices shown to the buyer at that moment",
		),
	}),
	TimelineStamps.extend({
		name: z.literal("order_action"),
		checkoutId: z
			.string()
			.min(1)
			.max(64)
			.nullish()
			.describe(
				"the checkout whose order these tickets came from — the same id the order itself lives at; null when the retrieval happens on a ticket page opened from its access link alone, which carries the capability token and no joinable id",
			),
		action: OrderActionSchema,
	}),
	TimelineStamps.extend({
		name: z.literal("checkout_stage"),
		checkoutId: z
			.string()
			.min(1)
			.max(64)
			.describe(
				"the cart this milestone belongs to — an opaque grouping key that holds one cart's stages together, never dereferenced into the checkout document itself",
			),
		eventId: z
			.string()
			.min(1)
			.max(64)
			.describe("the event whose tickets this cart holds"),
		stage: CheckoutStageSchema,
		reason: RefusalReasonSchema.nullish().describe(
			"why the cart was refused — the same closed vocabulary the refusal itself records; absent on every stage but a refusal",
		),
		startedAt: z
			.custom<Timestamp>()
			.describe(
				"when the cart itself was opened, carried on every one of its milestones — each funnel band is attributed to the day this instant falls in, never the day the milestone fired, so the bands stay nested inside a day: a cart begun at 23:52 and paid at 00:01 counts its contact and its payment on the one day it started, rather than reporting a payment on a day whose contact band never saw it",
			),
		utmSource: z
			.string()
			.min(1)
			.max(UTM_VALUE_MAX_LENGTH)
			.nullish()
			.describe(
				"the campaign source that brought the visit this cart was opened in; absent on a cart opened in a visit that carried no campaign",
			),
		utmCampaign: z
			.string()
			.min(1)
			.max(UTM_VALUE_MAX_LENGTH)
			.nullish()
			.describe(
				"the campaign that brought the visit this cart was opened in, the axis that separates one campaign from every other sharing its source; absent on a cart opened in a visit that carried no campaign",
			),
	}),
]);
export type TimelineEvent = z.infer<typeof TimelineEventSchema>;

// workspaces/{orgId}/analyticsDaily/{date} — one closed day, derived once and
// kept because deriving it again would read the same expiring raw data. The
// report callable is the only writer and the only reader that trusts it;
// every figure here is reproducible from the timeline, checkouts and orders
// while the 90-day raw window still covers the day.
//
// Fields justified:
//   date              — the row's own document id, restated because the
//                       callable answers with rows the wire carries no id for
//   visitGapMinutes — what the row was derived under; a row derived under
//                       another gap is not an answer to a default-gap request
//   computedAt        — the freshness the callable re-reads before serving a
//                       still-open day from this row
//   clientSemanticsVersion — the visit-entry client-profile semantics used to
//                            derive the row; absent on legacy rows
//   orderEvidenceVersion — proves coverage was checked against captured orders
//                          as well as timeline events before this row was stored
//   coverage          — whether each reported metric has complete, partial, or
//                       unavailable evidence for the selected cohort
// Excluded:
//   revenue and refund totals — orders carry the money, read live at report
//     time so a later refund is never contradicted by a frozen copy
//   a per-buyer breakdown — this row is the aggregate; the individual view
//     reads the buyer documents themselves
//   a total-rows or per-axis total count — the axes below are each bounded by
//     their own top-twenty-plus-other rule, which is the bound worth stating
//   a per-campaign SPEND figure — what a campaign cost is Meta's answer, read
//     by the poller onto the campaign document itself (campaign.entity.ts);
//     a copy on a daily row would be a second answer to it, and the report
//     joins the two at read time rather than storing them together
export const AnalyticsDailySchema = z.object({
	date: CalendarDay,
	visitGapMinutes: VisitGapMinutes,
	computedAt: z
		.custom<Timestamp>()
		.describe(
			"when this row was derived — the freshness the callable weighs against buyer activity before it serves a still-open day from cache",
		),
	visits: Count.describe(
		"runs of activity separated by more than the gap above, across every buyer active on this day",
	),
	visitors: Count.describe("distinct buyers active on this day"),
	visitsLanding: Count.describe("visits that entered on the landing page"),
	visitsLandingThenEvent: Count.describe(
		"of those, visits that then reached an event page — the landing→event click-through numerator",
	),
	pageViews: z
		.partialRecord(PageSchema, Count)
		.describe("page views on this day, by storefront surface"),
	eventViews: z
		.record(z.string(), Count)
		.describe(
			"event-page views by event, the twenty busiest plus one `other` row carrying every remaining event's views — bounded so a catalog of any size leaves this document the same size",
		),
	utm: z
		.record(
			z.string(),
			z.object({
				visits: Count,
				checkouts: Count,
				paid: Count,
			}),
		)
		.describe(
			"campaign source to what it brought — visits, the checkouts they opened, the ones that paid; the twenty largest by visits plus one `other` row",
		),
	utmCampaigns: z
		.record(
			z.string(),
			z.object({
				visits: Count,
				checkouts: Count,
				paid: Count,
			}),
		)
		.default({})
		.describe(
			"the same three figures split by `utm_campaign` rather than by source, so a paid campaign's own traffic is separable from every other campaign sharing its source — Meta substitutes `{{site_source_name}}` into `utm_source`, which resolves to one of five two-letter platform codes and is therefore the SAME value for every campaign this deployment ever runs. The twenty largest by visits plus one `other` row, the sibling axis's own bound. Defaulted rather than required: every row derived before this axis existed is still a row, and re-deriving one only to add an empty map would read raw data that has since expired",
		),
	funnel: z
		.object({
			eventPageLands: Count,
			contactCompleted: Count,
			authorising: Count,
			paid: Count,
			refused: z
				.partialRecord(RefusalReasonSchema, Count)
				.describe(
					"refusals by the reason the checkout itself recorded — the same closed vocabulary a refused checkout carries",
				),
			abandoned: Count,
			expired: Count,
		})
		.describe(
			"the purchase funnel on this day, buyer-driven checkouts only — an operator-issued checkout is not a visit's outcome",
		),
	droppedAt: z
		.partialRecord(FunnelStageSchema.exclude(["paid"]), Count)
		.describe(
			"visits by the deepest stage they reached without paying — a visit that paid is counted in the funnel above, not here",
		),
	orderActions: z
		.partialRecord(OrderActionSchema, Count)
		.describe("ticket retrievals on this day, by the channel that served them"),
	medianDwellSeconds: Count.describe(
		"the median visit's length in whole seconds, counting visible-tab time only",
	),
	newBuyers: Count.describe("buyers whose record was born on this day"),
	returningBuyers: Count.describe(
		"buyers active on this day whose record was born earlier",
	),
	visitsByGap: z
		.array(z.object({ minutes: VisitGapMinutes, visits: Count }).strict())
		.describe(
			"the same evidence regrouped at each rung of SENSITIVITY_GAPS, ascending — how much the visit count depends on where the gap is set. Stored beside the row rather than derived on request because a cached day reads no timeline, and a curve that vanished whenever the cache answered would be missing exactly when it is cheapest to have",
		),
	clientSemanticsVersion: ClientSemanticsVersionSchema.nullish().describe(
		"the visit-entry client-profile semantics used to derive this row; absent on legacy cache rows",
	),
	orderEvidenceVersion: z
		.literal(1)
		.nullish()
		.describe(
			"coverage was derived from captured orders and timeline evidence together; unstamped rows are rechecked while their raw evidence is retained",
		),
	coverage: ReportCoverageSchema.nullish().describe(
		"whether each metric has complete, partial, or unavailable evidence for the selected visit cohort; absent on legacy cache rows",
	),
	clientSpecs: ClientCountsSchema.nullish(),
});

/** The gaps the sensitivity curve is derived at. The knob itself is a
 *  continuum of 240 rungs; a curve over all of them is a wall of numbers, and
 *  a curve over these five is the question an operator actually has — does
 *  this figure survive a different definition of "a visit". */
export const SENSITIVITY_GAPS: readonly number[] = [5, 15, 30, 60, 120];
export type AnalyticsDaily = z.infer<typeof AnalyticsDailySchema>;

export const Buyer = entity<Actor>()({
	schema: BuyerSchema,
	actions: ["read", "list"] as const,
})
	.pipe(({ can, hasPermission, and, pathVar }) => [
		// The visitor's own record, by id. Data-free, so the emitter keeps it
		// list-safe; a collection query leaves the wildcard unbound, so it
		// opens nothing beyond the single document whose id is the caller's uid.
		// Its position is load-bearing: it emits first in the `||`, so a
		// storefront read is answered without ever reaching the permission
		// clause's own document get.
		can("read").when((_data, me) => me.uid.eq(pathVar("buyerId"))),
		// Operator: the live panel and the per-customer view. Data-free too, so
		// `list` is declared beside `read` — the emitter folds a data-free read
		// into list eligibility, and the kernel consults only rules that name
		// the action.
		can("read").when((_data, me) =>
			and(
				hasPermission(me.permissions, "tickets.reports.view"),
				me.mfa.eq(true),
			),
		),
		can("list").when((_data, me) =>
			and(
				hasPermission(me.permissions, "tickets.reports.view"),
				me.mfa.eq(true),
			),
		),
	])
	.adaptor(
		firestore({
			hierarchicalGrants: true,
			path: "workspaces/{orgId}/buyers/{buyerId}",
		}),
	);

export const TimelineEvent = entity<Actor>()({
	schema: TimelineEventSchema,
	actions: ["read"] as const,
})
	.pipe(({ can, hasPermission, and }) => [
		can("read").when((_data, me) =>
			and(
				hasPermission(me.permissions, "tickets.reports.view"),
				me.mfa.eq(true),
			),
		),
	])
	.adaptor(
		firestore({
			hierarchicalGrants: true,
			path: "workspaces/{orgId}/buyers/{buyerId}/timeline/{timelineEventId}",
		}),
	);

export const AnalyticsDaily = entity<Actor>()({
	schema: AnalyticsDailySchema,
	actions: ["read"] as const,
})
	.pipe(({ can, hasPermission, and }) => [
		can("read").when((_data, me) =>
			and(
				hasPermission(me.permissions, "tickets.reports.view"),
				me.mfa.eq(true),
			),
		),
	])
	.adaptor(
		firestore({
			hierarchicalGrants: true,
			path: "workspaces/{orgId}/analyticsDaily/{date}",
		}),
	);
